import { useBrowseSubscriptionPlanApi } from '@aibees/shared/api';
import { Plan, PlanInterval } from '@aibees/shared/definition';
import { useCurrentSubscriptionApi } from '@aibees/shared/features/price-and-plan/api/use-current-subscription-api';
import { useMemo } from 'react';

const seatsOf = (plan: Plan) => Number(plan.metadata?.seat) || 0;

// Real payloads have arrived in both casings; the query param expects upper.
const normalizeInterval = (interval?: PlanInterval) =>
  interval ? ((interval as string).toUpperCase() as PlanInterval) : undefined;

/**
 * Name of the cheapest public plan that grants more seats than the current one
 * — the plan the seat-limit dialog invites the user to move to. `undefined`
 * when it can't be resolved, which degrades the title to "a higher plan".
 */
export function useNextSeatPlanName(currentSeat?: number) {
  const { data: subscription } = useCurrentSubscriptionApi();
  const interval = normalizeInterval(subscription?.current?.plan?.interval);
  const { data: plans } = useBrowseSubscriptionPlanApi(interval);

  return useMemo(() => {
    if (!plans?.length) return undefined;

    const candidates = plans
      .filter(
        (plan) =>
          plan.active === true &&
          plan.metadata?.is_public === 'true' &&
          seatsOf(plan) > (currentSeat ?? 0)
      )
      // Cheapest first. Every candidate shares the current interval, so `amount`
      // is comparable here whatever its unit; ties fall back to the smaller
      // seat bump.
      .sort((a, b) => a.amount - b.amount || seatsOf(a) - seatsOf(b));

    return candidates[0]?.product?.name || undefined;
  }, [plans, currentSeat]);
}
